// src/toolkitServices/unlabelledApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ------------------------------ helpers ------------------------------ */
const str = (x) => (x == null ? "" : String(x));
const key = (x) => String(x ?? "");
const sameId = (a, b) => key(a) === key(b);
const sameDoc = (it, id) => sameId(it?._id ?? it?.id, id);
const toIdSet = (ids) => new Set((ids || []).map(key));
const normLimit = (args, draft) =>
  Number(args?.limit ?? draft?.pagination?.limit ?? draft?.limit ?? 10) || 10;

const pickData = (res) => {
  // tolerate {data}, {entry}, or raw object/array
  const d = res?.data ?? res?.entry ?? res;
  return d;
};

// Check if an item satisfies the active filters of a cached query
const matchesFilters = (item = {}, args = {}) => {
  const {
    search = "",
    status = "",
    vendorId = "",
    branchId = "",
    counterId = "",
    categoryId = "",
    productId = "",
    designId = "",
    purityId = "",
    dateFrom = "",
    dateTo = "",
  } = args || {};

  if (status && str(item.status) !== str(status)) return false;

  if (vendorId && !sameId(item.vendorId, vendorId)) return false;
  if (branchId && !sameId(item.branchId, branchId)) return false;
  if (counterId && !sameId(item.counterId, counterId)) return false;
  if (categoryId && !sameId(item.categoryId, categoryId)) return false;
  if (productId && !sameId(item.productId, productId)) return false;
  if (designId && !sameId(item.designId, designId)) return false;
  if (purityId && !sameId(item.purityId, purityId)) return false;

  if (dateFrom && item.createdAt && new Date(item.createdAt) < new Date(dateFrom))
    return false;
  if (dateTo && item.createdAt && new Date(item.createdAt) > new Date(dateTo))
    return false;

  if (search) {
    const q = search.toLowerCase();
    const hay = [
      item.description,
      item.vendorName,
      item.branchName,
      item.counterName,
      item.categoryName,
      item.productName,
      item.designName,
      item.purityName,
      item._id,
      item.id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (!hay.includes(q)) return false;
  }

  return true;
};

// keep list length under limit & adjust totals
const addItemsIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.data) || !newItems?.length) return;

  // If user is not on page 1, don't try to splice the array (just bump totals)
  if (args.page && Number(args.page) !== 1) {
    incrementTotals(draft, newItems.length);
    return;
  }

  // Add new items to the top
  draft.data.unshift(...newItems);

  // Trim to current limit
  const lim = normLimit(args, draft);
  if (lim > 0 && draft.data.length > lim) {
    draft.data.splice(lim);
  }

  incrementTotals(draft, newItems.length);
};

const incrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(1, Math.ceil(draft.pagination.totalDocs / lim));
  } else if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

const decrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(1, Math.ceil(draft.pagination.totalDocs / lim));
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

/* --------------------- transfer helpers (NEW) --------------------- */
const matchesTransferFilters = (tr = {}, args = {}) => {
  const { status = "", fromUserId = "", toUserId = "" } = args || {};
  if (status && String(tr?.status || "") !== String(status)) return false;
  if (fromUserId && !sameId(tr?.fromUserId, fromUserId)) return false;
  if (toUserId && !sameId(tr?.toUserId, toUserId)) return false;
  return true;
};

const addTransfersIntoListDraft = (draft, args, newTransfers = []) => {
  if (!Array.isArray(draft?.data) || !newTransfers?.length) return;

  if (args.page && Number(args.page) !== 1) {
    incrementTotals(draft, newTransfers.length);
    return;
  }

  draft.data.unshift(...newTransfers);

  const lim = normLimit(args, draft);
  if (lim > 0 && draft.data.length > lim) {
    draft.data.splice(lim);
  }

  incrementTotals(draft, newTransfers.length);
};
/* --------------------------------------------------------------------- */

export const unlabelledApi = createApi({
  reducerPath: "unlabelledApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),

  // ✅ FIX: add UnlabelledBucket tag
  tagTypes: ["UnlabelledItem", "UnlabelledTransfer", "UnlabelledBucket"],

  endpoints: (builder) => ({
    /* ----------------------------- CREATE ----------------------------- */
    // POST /unlabelled-stock/create
    addUnlabelledStock: builder.mutation({
      query: (payload) => ({
        url: "/unlabelled-stock/create",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const maybe = pickData(res);
          const created = Array.isArray(maybe)
            ? maybe
            : Array.isArray(res?.entries)
            ? res.entries
            : [];
          if (!created.length) return;

          const allArgs = unlabelledApi.util.selectCachedArgsForQuery(
            getState(),
            "getAllUnlabelledStock"
          );

          for (const args of allArgs) {
            const addable = created.filter((it) => matchesFilters(it, args));
            if (!addable.length) continue;

            dispatch(
              unlabelledApi.util.updateQueryData("getAllUnlabelledStock", args, (draft) => {
                addItemsIntoListDraft(draft, args, addable);
              })
            );
          }
        } catch {
          /* handled by UI */
        }
      },
    }),

    /* ------------------------------ LIST ------------------------------ */
    // GET /unlabelled-stock?...
    getAllUnlabelledStock: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 10,
          search = "",
          status = "",
          vendorId = "",
          branchId = "",
          counterId = "",
          categoryId = "",
          productId = "",
          designId = "",
          purityId = "",
          dateFrom = "",
          dateTo = "",
          sortBy,
          sortOrder,
        } = args;

        const params = { page, limit, q: search };
        if (status) params.status = status;
        if (vendorId) params.vendor = vendorId;
        if (branchId) params.branch = branchId;
        if (counterId) params.counter = counterId;
        if (categoryId) params.category = categoryId;
        if (productId) params.product = productId;
        if (designId) params.design = designId;
        if (purityId) params.purity = purityId;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;

        return { url: "/unlabelled-stock", method: "GET", params };
      },
      providesTags: ["UnlabelledItem"],

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.search,
          a.status,
          a.vendorId,
          a.branchId,
          a.counterId,
          a.categoryId,
          a.productId,
          a.designId,
          a.purityId,
          a.dateFrom,
          a.dateTo,
          a.sortBy,
          a.sortOrder,
        ].join("|");
      },

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    /* ----------------------------- GET ONE --------------------------- */
    getUnlabelledById: builder.query({
      query: (id) => `/unlabelled-stock/${id}`,
      providesTags: (_res, _err, id) => [{ type: "UnlabelledItem", id: key(id) }],
    }),

    /* ----------------------------- UPDATE ---------------------------- */
    updateUnlabelledStock: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/unlabelled-stock/${id}`,
        method: "PUT",
        body: patch,
      }),
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = unlabelledApi.util.selectCachedArgsForQuery(
          getState(),
          "getAllUnlabelledStock"
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              unlabelledApi.util.updateQueryData("getAllUnlabelledStock", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const idx = draft.data.findIndex((it) => sameDoc(it, id));
                if (idx >= 0) {
                  draft.data[idx] = { ...draft.data[idx], ...patch };
                }
              })
            )
          );
        }

        patches.push(
          dispatch(
            unlabelledApi.util.updateQueryData("getUnlabelledById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, patch);
            })
          )
        );

        try {
          const { data: res } = await queryFulfilled;
          const updated = pickData(res);

          if (updated) {
            for (const args of allArgs) {
              dispatch(
                unlabelledApi.util.updateQueryData("getAllUnlabelledStock", args, (draft) => {
                  if (!Array.isArray(draft?.data)) return;
                  const idx = draft.data.findIndex((it) => sameDoc(it, id));
                  const nowMatches = matchesFilters(updated, args);

                  if (idx >= 0) {
                    if (nowMatches) {
                      draft.data[idx] = { ...draft.data[idx], ...updated };
                    } else {
                      draft.data.splice(idx, 1);
                      decrementTotals(draft, 1);
                    }
                  } else if (nowMatches) {
                    addItemsIntoListDraft(draft, args, [updated]);
                  }
                })
              );
            }

            dispatch(
              unlabelledApi.util.updateQueryData("getUnlabelledById", id, (draft) => {
                if (draft?.data) Object.assign(draft.data, updated);
              })
            );
          }
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ----------------------------- DELETE ---------------------------- */
    deleteUnlabelledItem: builder.mutation({
      query: (id) => ({
        url: `/unlabelled-stock/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = unlabelledApi.util.selectCachedArgsForQuery(
          getState(),
          "getAllUnlabelledStock"
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              unlabelledApi.util.updateQueryData("getAllUnlabelledStock", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => !sameDoc(it, id));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* -------------------------- BULK DELETE -------------------------- */
    bulkDeleteUnlabelled: builder.mutation({
      query: (ids) => ({
        url: `/unlabelled-stock/bulk-delete`,
        method: "POST",
        body: { ids },
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const idSet = toIdSet(ids);
        const allArgs = unlabelledApi.util.selectCachedArgsForQuery(
          getState(),
          "getAllUnlabelledStock"
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              unlabelledApi.util.updateQueryData("getAllUnlabelledStock", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => !idSet.has(key(it?._id ?? it?.id)));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ====================== UNLABELLED TRANSFER ====================== */

    // POST /unlabelled-transfer/create
    createUnlabelledTransfer: builder.mutation({
      query: (body) => ({
        url: "/unlabelled-transfer/create",
        method: "POST",
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = pickData(res);
          if (!created) return;

          const allArgs = unlabelledApi.util.selectCachedArgsForQuery(
            getState(),
            "listUnlabelledTransfers"
          );

          for (const args of allArgs) {
            if (!matchesTransferFilters(created, args)) continue;
            dispatch(
              unlabelledApi.util.updateQueryData("listUnlabelledTransfers", args, (draft) =>
                addTransfersIntoListDraft(draft, args, [created])
              )
            );
          }
        } catch {
          /* handled by UI */
        }
      },
    }),

    // GET /unlabelled-transfer?status=&toUserId=&fromUserId=&page=&limit=
    listUnlabelledTransfers: builder.query({
      query: (args = {}) => {
        const { page = 1, limit = 20, status = "", fromUserId = "", toUserId = "" } = args;
        const params = { page, limit };
        if (status) params.status = status;
        if (fromUserId) params.fromUserId = fromUserId;
        if (toUserId) params.toUserId = toUserId;

        return { url: "/unlabelled-transfer", method: "GET", params };
      },
      providesTags: ["UnlabelledTransfer"],

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [endpointName, a.page, a.limit, a.status, a.fromUserId, a.toUserId].join("|");
      },

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    // GET /unlabelled-transfer/:id
    getUnlabelledTransferById: builder.query({
      query: (id) => `/unlabelled-transfer/${id}`,
      providesTags: (_res, _err, id) => [{ type: "UnlabelledTransfer", id: key(id) }],
    }),

    // POST /unlabelled-transfer/respond
    respondUnlabelledTransfer: builder.mutation({
      query: (body) => ({
        url: "/unlabelled-transfer/respond",
        method: "POST",
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled, getState }) {
        const transferId = body?.transferId || body?.id;
        if (!transferId) {
          try {
            await queryFulfilled;
          } catch {}
          return;
        }

        const patches = [];
        const allArgs = unlabelledApi.util.selectCachedArgsForQuery(
          getState(),
          "listUnlabelledTransfers"
        );

        // optimistic: mark pending -> completed/rejected? (server final decides)
        for (const args of allArgs) {
          patches.push(
            dispatch(
              unlabelledApi.util.updateQueryData("listUnlabelledTransfers", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const idx = draft.data.findIndex((t) => sameDoc(t, transferId));
                if (idx < 0) return;
                draft.data[idx] = { ...draft.data[idx], _optimistic: true };
              })
            )
          );
        }

        patches.push(
          dispatch(
            unlabelledApi.util.updateQueryData("getUnlabelledTransferById", transferId, (draft) => {
              if (draft?.data) draft.data._optimistic = true;
            })
          )
        );

        try {
          const { data: res } = await queryFulfilled;
          const updated = pickData(res);

          if (updated) {
            for (const args of allArgs) {
              dispatch(
                unlabelledApi.util.updateQueryData("listUnlabelledTransfers", args, (draft) => {
                  if (!Array.isArray(draft?.data)) return;
                  const idx = draft.data.findIndex((t) => sameDoc(t, transferId));
                  const nowMatches = matchesTransferFilters(updated, args);

                  if (idx >= 0) {
                    if (nowMatches) {
                      draft.data[idx] = { ...draft.data[idx], ...updated, _optimistic: false };
                    } else {
                      draft.data.splice(idx, 1);
                      decrementTotals(draft, 1);
                    }
                  } else if (nowMatches) {
                    addTransfersIntoListDraft(draft, args, [updated]);
                  }
                })
              );
            }

            dispatch(
              unlabelledApi.util.updateQueryData("getUnlabelledTransferById", transferId, (draft) => {
                if (draft?.data) Object.assign(draft.data, updated);
              })
            );
          }
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    // DELETE /unlabelled-transfer/:id
    deleteUnlabelledTransfer: builder.mutation({
      query: (id) => ({
        url: `/unlabelled-transfer/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = unlabelledApi.util.selectCachedArgsForQuery(
          getState(),
          "listUnlabelledTransfers"
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              unlabelledApi.util.updateQueryData("listUnlabelledTransfers", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((t) => !sameDoc(t, id));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ====================== UNLABELLED BUCKETS ✅ ====================== */
    // ✅ NOTE:
    // If your backend router is mounted at "/unlabelled-stock",
    // then the correct endpoint should be "/unlabelled-stock/buckets"
    // (NOT "/unlabelled-buckets").
    getAllUnlabelledBuckets: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 50,
          search = "",
          branchId = "",
          counterId = "",
          categoryId = "",
          productId = "",
          designId = "",
          purityId = "",
          isActive = "true",
        } = args;

        const params = { page, limit, q: search, isActive };
        if (branchId) params.branchId = branchId;
        if (counterId) params.counterId = counterId;
        if (categoryId) params.categoryId = categoryId;
        if (productId) params.productId = productId;
        if (designId) params.designId = designId;
        if (purityId) params.purityId = purityId;

        // ✅ FIXED URL:
        return { url: "/unlabelled-stock/buckets", method: "GET", params };
      },

      providesTags: ["UnlabelledBucket"],

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.search,
          a.branchId,
          a.counterId,
          a.categoryId,
          a.productId,
          a.designId,
          a.purityId,
          a.isActive,
        ].join("|");
      },

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),
  }),
});

export const {
  useAddUnlabelledStockMutation,
  useGetAllUnlabelledStockQuery,
  useGetUnlabelledByIdQuery,
  useUpdateUnlabelledStockMutation,
  useDeleteUnlabelledItemMutation,
  useBulkDeleteUnlabelledMutation,

  // ✅ buckets
  useGetAllUnlabelledBucketsQuery,

  // transfers
  useCreateUnlabelledTransferMutation,
  useListUnlabelledTransfersQuery,
  useGetUnlabelledTransferByIdQuery,
  useRespondUnlabelledTransferMutation,
  useDeleteUnlabelledTransferMutation,
} = unlabelledApi;

export default unlabelledApi;
